import * as React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AgreementStatus, PartnerType, partnerCreateSchema, shortCodeFromName } from '@clawhire/shared';
import { api, isApiError } from '@/lib/api';
import { enumOptions } from '@/lib/format';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { SimpleSelect } from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { toast } from '@/components/ui/toaster';
import type { Partner } from '@/pages/partners/types';

/** Form values are plain strings; `partnerCreateSchema` turns them into the API body on submit. */
const formSchema = z.object({
  name: z.string().trim().min(1, 'Name is required').max(120, 'At most 120 characters'),
  type: PartnerType.schema,
  shortCode: z.string().trim().max(40, 'At most 40 characters'),
  contactPerson: z.string().trim().max(120),
  email: z.string().trim().email('Enter a valid e-mail').or(z.literal('')),
  phone: z.string().trim().max(60),
  agreement: AgreementStatus.schema,
  terms: z.string().trim().max(2000),
  notes: z.string().trim().max(4000),
});
type FormValues = z.infer<typeof formSchema>;

const DEFAULTS: FormValues = {
  name: '',
  type: 'SUPPLIER',
  shortCode: '',
  contactPerson: '',
  email: '',
  phone: '',
  agreement: 'NONE',
  terms: '',
  notes: '',
};

export interface PartnerCreateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called with the created partner (the caller navigates / refreshes). */
  onCreated?: (partner: Partner) => void;
}

/** "New partner" dialog: `POST /partners`. The short code is suggested from the name until edited. */
export function PartnerCreateDialog({ open, onOpenChange, onCreated }: PartnerCreateDialogProps) {
  const queryClient = useQueryClient();
  const form = useForm<FormValues>({ resolver: zodResolver(formSchema), defaultValues: DEFAULTS });
  const { reset, setValue, setError, watch, control, handleSubmit, formState } = form;
  const [codeTouched, setCodeTouched] = React.useState(false);
  const name = watch('name');

  React.useEffect(() => {
    if (open) {
      reset(DEFAULTS);
      setCodeTouched(false);
    }
  }, [open, reset]);

  React.useEffect(() => {
    if (!codeTouched) setValue('shortCode', name.trim() ? shortCodeFromName(name) : '');
  }, [name, codeTouched, setValue]);

  const create = useMutation({
    mutationFn: (values: FormValues) => api.post<Partner>('/partners', partnerCreateSchema.parse(values)),
    onSuccess: async (partner) => {
      await queryClient.invalidateQueries({ queryKey: ['partners'] });
      toast.success(`Partner "${partner.name}" created`);
      onOpenChange(false);
      onCreated?.(partner);
    },
    onError: (err: Error) => {
      if (isApiError(err)) {
        const fields = err.fieldErrors();
        for (const [path, message] of Object.entries(fields)) {
          if (path in DEFAULTS) setError(path as keyof FormValues, { message });
        }
        if (err.status === 409) {
          setError('name', { message: err.message });
          return;
        }
        if (Object.keys(fields).length > 0) return;
      }
      toast.error(err.message);
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl">
        <Form {...form}>
          <form onSubmit={handleSubmit((v) => create.mutate(v))} className="space-y-4" noValidate>
            <DialogHeader>
              <DialogTitle>New partner</DialogTitle>
              <DialogDescription>An intermediary, a supplier or a freelancer.</DialogDescription>
            </DialogHeader>

            <div className="grid gap-4 sm:grid-cols-2">
              <FormField
                control={control}
                name="name"
                render={({ field }) => (
                  <FormItem className="sm:col-span-2">
                    <FormLabel>Name</FormLabel>
                    <FormControl>
                      <Input autoFocus placeholder="Amber Digital" maxLength={120} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={control}
                name="type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Type</FormLabel>
                    <FormControl>
                      <SimpleSelect
                        id={field.name}
                        value={field.value}
                        onChange={(v) => field.onChange(v ?? 'SUPPLIER')}
                        options={enumOptions(PartnerType)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={control}
                name="shortCode"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Short code</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="AmberDigital"
                        maxLength={40}
                        {...field}
                        onChange={(e) => {
                          setCodeTouched(true);
                          field.onChange(e);
                        }}
                      />
                    </FormControl>
                    <FormDescription>Used in folder and file names; suggested from the name.</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={control}
                name="contactPerson"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Contact person</FormLabel>
                    <FormControl>
                      <Input maxLength={120} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>E-mail</FormLabel>
                    <FormControl>
                      <Input type="email" autoComplete="off" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={control}
                name="phone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Phone</FormLabel>
                    <FormControl>
                      <Input type="tel" maxLength={60} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={control}
                name="agreement"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Agreement</FormLabel>
                    <FormControl>
                      <SimpleSelect
                        id={field.name}
                        value={field.value}
                        onChange={(v) => field.onChange(v ?? 'NONE')}
                        options={enumOptions(AgreementStatus)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={control}
                name="terms"
                render={({ field }) => (
                  <FormItem className="sm:col-span-2">
                    <FormLabel>Terms</FormLabel>
                    <FormControl>
                      <Textarea
                        rows={2}
                        placeholder="Payment terms, margin agreements, exclusivity…"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={control}
                name="notes"
                render={({ field }) => (
                  <FormItem className="sm:col-span-2">
                    <FormLabel>Notes</FormLabel>
                    <FormControl>
                      <Textarea rows={3} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" loading={create.isPending || formState.isSubmitting}>
                Create partner
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
